<script setup lang="ts">
import { onMounted, onBeforeUnmount, watch } from 'vue';
import { RouterView } from 'vue-router';
import AppNotification from '@/components/shared/AppNotification.vue';

import { useNetworkStore } from '@/stores/network';
import { useNotificationStore } from '@/stores/notification';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { authSyncService, AuthSyncEventType } from '@/services/auth-sync.service';
import { useLangStore } from '@/stores/language';
import { i18n } from '@/plugins/i18n';

import vuetify from '@/plugins/vuetify';

const { t } = useI18n();
const network = useNetworkStore();
const authStore = useAuthStore();
const { showErrorNotification, showSuccessNotification } = useNotificationStore();
const langStore = useLangStore();

let unsubscribeAuthSync: (() => void) | null = null;

const handleOffline = () => {
  if (network.online) {
    network.setOnline(false);
    showErrorNotification(t('notifications.connection_lost'));
  }
};

const handleOnline = () => {
  if (!network.online) {
    network.setOnline(true);
    showSuccessNotification(t('notifications.connection_restored'));
  }
};

watch(
  () => langStore.language,
  (lang) => {
    i18n.global.locale.value = lang;

    if (vuetify.locale) {
      vuetify.locale.current.value = lang;
    }
  },
  { immediate: true },
);

onMounted(() => {
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  unsubscribeAuthSync = authSyncService.subscribe((event) => {
    switch (event.type) {
      case AuthSyncEventType.LOGIN:
        authStore.handleExternalLogin(event.jwt);
        break;

      case AuthSyncEventType.LOGOUT:
        authStore.handleExternalLogout();
        break;

      case AuthSyncEventType.TOKEN_REFRESH:
        authStore.handleExternalTokenRefresh(event.jwt);
        break;
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('offline', handleOffline);
  window.removeEventListener('online', handleOnline);

  if (unsubscribeAuthSync) {
    unsubscribeAuthSync();
  }
});
</script>

<template>
  <RouterView />
  <AppNotification />
</template>
